// The quote pipeline strip (Session ASH, Q-7): one row of stage totals above the Quotes list, covering EVERY
// maintenance quote, project quotation and repair estimate (GET /api/quotes/pipeline). Pressing a stage lists what is
// in it. The stages and their words come from the server, which the client mirror in src/lib/quoteFunnel.ts is
// parity-tested against.
//
// Values differ in kind and the strip says so: a maintenance quote is an ANNUAL figure, a project or a repair
// estimate a ONE-OFF figure. Same-tier renewal records are not in any stage (they are renewals completing, not new
// work); they are counted in a line of their own.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { formatDate } from "../../lib/format";
import type { QuotePipeline, QuotePipelineItem, PipelineStageKey, PipelineSourceKey } from "../../lib/api";

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

const SOURCE_LABEL: Record<PipelineSourceKey, string> = {
  quote: "maintenance",
  project: "project",
  repair_estimate: "repair",
};

const STAGE_TEXT: Record<PipelineStageKey, string> = {
  drafting: "text-ele-strong",
  with_customer: "text-ele-strong",
  won: "text-ele-green",
  lost: "text-ele-danger",
  closed: "text-ele-muted",
};

function ItemRef({ item }: { item: QuotePipelineItem }) {
  if (item.source === "quote") {
    return (
      <Link to={`/office/quotes/${encodeURIComponent(item.id)}`} className="ele-ref underline-offset-2 hover:underline">
        {item.ref}
      </Link>
    );
  }
  if (item.source === "project") {
    return (
      <Link to={`/office/projects/${encodeURIComponent(item.id)}`} className="ele-ref underline-offset-2 hover:underline">
        {item.ref}
      </Link>
    );
  }
  return <span className="ele-ref">{item.ref}</span>;
}

export function QuotePipelineStrip() {
  const [data, setData] = useState<QuotePipeline | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<PipelineStageKey | null>(null);

  useEffect(() => {
    let live = true;
    api.quotes
      .pipeline()
      .then((d) => {
        if (live) setData(d);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, []);

  if (failed) {
    return (
      <p role="alert" className="mb-4 text-sm text-ele-danger">
        The pipeline could not be loaded. The list below is unaffected.
      </p>
    );
  }
  if (!data) return <div aria-busy="true" className="mb-4 h-24 rounded-ele border border-ele-hairline bg-ele-surface" />;

  const items = open ? data.items.filter((i) => i.stage === open) : [];

  return (
    <section aria-label="Quote pipeline" className="mb-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {data.stages.map((s) => {
          const parts = (Object.keys(s.bySource) as PipelineSourceKey[])
            .filter((k) => s.bySource[k].count > 0)
            .map((k) => `${s.bySource[k].count} ${SOURCE_LABEL[k]}`);
          const pressed = open === s.stage;
          return (
            <button
              key={s.stage}
              type="button"
              aria-pressed={pressed}
              onClick={() => setOpen(pressed ? null : s.stage)}
              className={`rounded-ele border p-3 text-left transition-colors hover:bg-ele-row-hover ${
                pressed ? "border-ele-purple-accent bg-ele-active-wash" : "border-ele-hairline bg-ele-surface"
              }`}
            >
              <span className="block font-menu text-xs uppercase tracking-wide text-ele-muted">{s.label}</span>
              <span className={`mt-1 block font-heading text-2xl ${STAGE_TEXT[s.stage]}`}>{s.count}</span>
              <span className="block text-sm text-ele-body">{gbp(s.value)}</span>
              <span className="mt-1 block text-xs text-ele-faint">{parts.length ? parts.join(" · ") : "None"}</span>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-ele-faint">
        Maintenance values are yearly; project and repair values are one-off.
        {data.renewals.count > 0 &&
          ` ${data.renewals.count} same-tier renewal${data.renewals.count === 1 ? "" : "s"} (${gbp(data.renewals.value)} a year) ${
            data.renewals.count === 1 ? "is" : "are"
          } counted separately, not as new work.`}
      </p>

      {open && (
        <div className="mt-3 rounded-ele border border-ele-hairline bg-ele-surface p-3">
          <h2 className="font-heading text-base text-ele-strong">
            {data.stages.find((s) => s.stage === open)?.label}: {items.length}
          </h2>
          {items.length === 0 ? (
            <p className="mt-2 text-sm text-ele-body">Nothing at this stage.</p>
          ) : (
            <table className="ele-list-table mt-2 w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-ele-muted">
                  <th className="py-1 pr-2 font-normal">Reference</th>
                  <th className="py-1 pr-2 font-normal">Kind</th>
                  <th className="py-1 pr-2 font-normal">Customer</th>
                  <th className="py-1 pr-2 font-normal">Value</th>
                  <th className="py-1 pr-2 font-normal">Owner</th>
                  <th className="py-1 font-normal">Date</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={`${i.source}:${i.id}`} className="border-t border-ele-hairline-soft">
                    <td className="truncate py-1 pr-2">
                      <ItemRef item={i} />
                    </td>
                    <td className="truncate py-1 pr-2 text-ele-body">{SOURCE_LABEL[i.source]}</td>
                    <td className="truncate py-1 pr-2 text-ele-body">{i.customerName ?? i.siteName ?? "Not recorded"}</td>
                    <td className="truncate py-1 pr-2 text-ele-body">
                      {i.value == null ? "Not priced" : `${gbp(i.value)}${i.valueBasis === "annual" ? " a year" : ""}`}
                    </td>
                    <td className="truncate py-1 pr-2 text-ele-body">{i.ownerName ?? ""}</td>
                    <td className="truncate py-1 text-ele-body">{formatDate(i.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
